// pages/StageManager.js
// Broker-only page for managing deal stage templates — now product-aware.
// Route: /stages
// Accessible to owner & manager roles only (adviser gets redirected).
//
// Each product type (residential, btl, protection, etc.) has its own
// ordered workflow. Stages can trigger an email on entry, be hidden from
// the introducer view, and one can be marked the terminal (final) stage.

import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { usePermissions } from '../hooks/usePermissions';

const token = () => localStorage.getItem('token');

const RAG_OPTIONS = [
  { value: 'red',   label: 'Red',   dot: '#ef4444', bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  { value: 'amber', label: 'Amber', dot: '#f59e0b', bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  { value: 'green', label: 'Green', dot: '#10b981', bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
];

// Product workflows the broker can configure independently.
const PRODUCT_TYPES = [
  { value: 'residential',  label: 'Residential Mortgage' },
  { value: 'btl',          label: 'Buy to Let' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'protection',   label: 'Protection' },
  { value: 'investment',   label: 'Investment' },
  { value: 'pension',      label: 'Pension' },
];

const ragStyle = (colour) => {
  const r = RAG_OPTIONS.find(o => o.value === colour) || RAG_OPTIONS[1];
  return { background: r.bg, border: `1px solid ${r.border}`, color: r.text };
};

// ── Sub-component: inline editable stage row ──────────────────
function StageRow({ stage, templates, onSave, onDelete, onDragStart, onDragOver, onDrop, dragging }) {
  const [editing, setEditing] = useState(false);
  const [name, setName]       = useState(stage.name);
  const [colour, setColour]   = useState(stage.rag_colour);
  const [triggersEmail, setTriggersEmail] = useState(!!stage.triggers_email);
  const [emailTemplateId, setEmailTemplateId] = useState(stage.email_template_id || '');
  const [visibleToIntro, setVisibleToIntro] = useState(stage.visible_to_introducer !== false);
  const [isTerminal, setIsTerminal] = useState(!!stage.is_terminal);
  const [saving, setSaving]   = useState(false);
  const nameRef = useRef();

  const startEdit = () => { setEditing(true); setTimeout(() => nameRef.current?.focus(), 50); };
  const cancel = () => {
    setName(stage.name);
    setColour(stage.rag_colour);
    setTriggersEmail(!!stage.triggers_email);
    setEmailTemplateId(stage.email_template_id || '');
    setVisibleToIntro(stage.visible_to_introducer !== false);
    setIsTerminal(!!stage.is_terminal);
    setEditing(false);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave(stage.id, {
      name: name.trim(),
      rag_colour: colour,
      triggers_email: triggersEmail,
      email_template_id: triggersEmail ? (emailTemplateId || null) : null,
      visible_to_introducer: visibleToIntro,
      is_terminal: isTerminal,
    });
    setSaving(false);
    setEditing(false);
  };

  const ragDot = RAG_OPTIONS.find(o => o.value === stage.rag_colour);

  return (
    <div
      draggable={!editing}
      onDragStart={() => onDragStart(stage.id)}
      onDragOver={e => { e.preventDefault(); onDragOver(stage.id); }}
      onDrop={() => onDrop(stage.id)}
      style={{
        display: 'flex', flexDirection: 'column', gap: 0,
        padding: '12px 16px',
        background: dragging ? '#f0fdf4' : '#fff',
        border: '1px solid #E2E8F0', borderRadius: 8, marginBottom: 8,
        cursor: editing ? 'default' : 'grab',
        transition: 'background 0.15s, box-shadow 0.15s',
        boxShadow: dragging ? '0 2px 8px rgba(16,185,129,0.15)' : 'none',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ color: '#CBD5E0', fontSize: 16, cursor: 'grab', flexShrink: 0 }}>⠿</span>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: ragDot?.dot || '#f59e0b', flexShrink: 0 }} />

        {editing ? (
          <>
            <input
              ref={nameRef}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
              style={{ flex: 1, border: '1px solid #10b981', borderRadius: 6, padding: '6px 10px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {RAG_OPTIONS.map(o => (
                <button key={o.value} type="button"
                  onClick={() => setColour(o.value)} title={o.label}
                  style={{
                    width: 22, height: 22, borderRadius: '50%',
                    border: colour === o.value ? `3px solid ${o.dot}` : '2px solid #E2E8F0',
                    background: o.dot, cursor: 'pointer', padding: 0, flexShrink: 0,
                    boxShadow: colour === o.value ? `0 0 0 2px ${o.bg}` : 'none',
                  }}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#0e1b2c' }}>
              {stage.name}
              {stage.is_terminal && (
                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '1px 6px', borderRadius: 10, textTransform: 'uppercase' }}>Final</span>
              )}
            </span>
            {stage.is_protected && (
              <span title="System stage — drives commission and reporting events; can be renamed but not deleted" style={{ fontSize: 13 }}>🔒</span>
            )}
            {stage.triggers_email && (
              <span title="Sends an email to the introducer on entry" style={{ fontSize: 13, color: '#10b981' }}>✉</span>
            )}
            {!stage.visible_to_introducer && (
              <span title="Hidden from introducer" style={{ fontSize: 13, color: '#a0aec0' }}>🚫</span>
            )}
            <span style={{ ...ragStyle(stage.rag_colour), fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize' }}>
              {stage.rag_colour}
            </span>
            <button onClick={startEdit}
              style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#4a5568', flexShrink: 0 }}>
              Edit
            </button>
            {!stage.is_protected && (
              <button
                onClick={() => window.confirm(`Delete stage "${stage.name}"?`) && onDelete(stage.id)}
                style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#ef4444', flexShrink: 0 }}>
                ✕
              </button>
            )}
          </>
        )}
      </div>

      {/* Expanded controls when editing */}
      {editing && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #EDF2F7', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4a5568' }}>
            <input type="checkbox" checked={triggersEmail} onChange={e => setTriggersEmail(e.target.checked)} style={{ accentColor: '#10b981' }} />
            Send email to introducer when a deal enters this stage
          </label>

          {triggersEmail && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4a5568', paddingLeft: 24 }}>
              Template:
              <select value={emailTemplateId} onChange={e => setEmailTemplateId(e.target.value)}
                style={{ border: '1px solid #CBD5E0', borderRadius: 6, padding: '5px 8px', fontSize: 13, fontFamily: 'inherit' }}>
                <option value="">{templates.length ? '— Select a template —' : 'No templates yet'}</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4a5568' }}>
            <input type="checkbox" checked={visibleToIntro} onChange={e => setVisibleToIntro(e.target.checked)} style={{ accentColor: '#10b981' }} />
            Visible to introducer
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4a5568' }}>
            <input type="checkbox" checked={isTerminal} onChange={e => setIsTerminal(e.target.checked)} style={{ accentColor: '#10b981' }} />
            Final stage (deal is closed/complete at this point)
          </label>

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={save} disabled={saving}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={cancel}
              style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#718096' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function StageManager() {
  const mobile = useMobile();
  const navigate = useNavigate();
  const perms = usePermissions();

  const [productType, setProductType] = useState('residential');
  const [stages, setStages]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [newName, setNewName] = useState('');
  const [newColour, setNewColour] = useState('amber');
  const [adding, setAdding]   = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const dragId = useRef(null);
  const overId = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const loadStages = useCallback(async (pt) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/stages?product_type=${encodeURIComponent(pt)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setStages(data.stages || []);
      setError('');
    } catch { setError('Failed to load stages'); }
    finally { setLoading(false); }
  }, []);

  // Load email templates (used by the per-stage email trigger picker).
  // Endpoint may not exist yet — fail silently so the page still works.
  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/email-templates', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(Array.isArray(data) ? data : (data.templates || []));
      }
    } catch { /* templates feature not built yet — ignore */ }
  }, []);

  useEffect(() => {
    if (!perms.canEditDeal && !perms.isPlatformOwner) {
      navigate('/deals');
      return;
    }
    loadTemplates();
    loadStages(productType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when the product workflow changes
  useEffect(() => {
    loadStages(productType);
  }, [productType, loadStages]);

  const handleSave = async (id, body) => {
    const res = await fetch(`/api/stages/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error || 'Save failed'); return; }
    setStages(s => s.map(st => st.id === id ? data.stage : st));
  };

  const handleDelete = async (id) => {
    const res = await fetch(`/api/stages/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) {
      // Surface the server's human-readable reason (protected / has trigger / in use)
      const data = await res.json().catch(() => ({}));
      setError(data.error || 'Delete failed');
      return;
    }
    setStages(s => s.filter(st => st.id !== id));
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await fetch('/api/stages', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), rag_colour: newColour, product_type: productType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add');
      setStages(s => [...s, data.stage]);
      setNewName(''); setNewColour('amber'); setShowAdd(false);
    } catch (err) { setError(err.message); }
    finally { setAdding(false); }
  };

  // ── Drag & drop reorder (scoped to current product) ──
  const handleDragStart = (id) => { dragId.current = id; };
  const handleDragOver  = (id) => { overId.current = id; setDragOver(id); };

  const handleDrop = async () => {
    setDragOver(null);
    if (!dragId.current || dragId.current === overId.current) return;

    const fromIdx = stages.findIndex(s => s.id === dragId.current);
    const toIdx   = stages.findIndex(s => s.id === overId.current);
    if (fromIdx < 0 || toIdx < 0) return;

    const reordered = [...stages];
    const [moved]   = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setStages(reordered);

    try {
      const res = await fetch('/api/stages/reorder', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds: reordered.map(s => s.id), product_type: productType }),
      });
      const data = await res.json();
      if (res.ok) setStages(data.stages);
    } catch { setError('Reorder failed — refresh to resync'); }

    dragId.current = null; overId.current = null;
  };

  const currentProductLabel = PRODUCT_TYPES.find(p => p.value === productType)?.label || productType;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <Sidebar />
      <main style={{ flex: 1, marginLeft: mobile ? 0 : 70, padding: '32px 40px', width: mobile ? '100%' : 'calc(100% - 70px)', boxSizing: 'border-box' }}>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: '#0e1b2c' }}>Deal Stages</h1>
            <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>
              Define a separate pipeline for each product. Drag to reorder. Introducers see visible stages read-only on their deals.
            </p>
          </div>
          <button onClick={() => navigate('/deals')}
            style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 8, padding: '8px 16px', fontSize: 13, color: '#718096', cursor: 'pointer' }}>
            ← Back to Deals
          </button>
        </div>

        {error && (
          <div style={{ background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16, fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
            ⚠️ {error}
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c53030', fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>
        )}

        <div style={{ maxWidth: 640 }}>

          {/* Product workflow selector */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#0e1b2c', marginBottom: 6 }}>Product workflow</label>
            <select
              value={productType}
              onChange={e => setProductType(e.target.value)}
              style={{ width: '100%', maxWidth: 320, border: '1px solid #CBD5E0', borderRadius: 8, padding: '9px 12px', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: '#0e1b2c' }}
            >
              {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          {/* RAG legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {RAG_OPTIONS.map(o => (
              <div key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4a5568' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: o.dot, display: 'inline-block' }} />
                {o.label}
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>Loading stages…</div>
          ) : stages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#718096', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10 }}>
              <p style={{ marginBottom: 12 }}>No stages yet for {currentProductLabel}. Add your first stage below.</p>
            </div>
          ) : (
            stages.map(stage => (
              <StageRow
                key={stage.id}
                stage={stage}
                templates={templates}
                onSave={handleSave}
                onDelete={handleDelete}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                dragging={dragOver === stage.id}
              />
            ))
          )}

          {/* Add stage */}
          {showAdd ? (
            <div style={{ background: '#fff', border: '1px solid #10b981', borderRadius: 8, padding: '16px', marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  autoFocus
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setShowAdd(false); setNewName(''); } }}
                  placeholder="Stage name, e.g. Offer Received"
                  style={{ flex: 1, border: '1px solid #CBD5E0', borderRadius: 6, padding: '8px 12px', fontSize: 14, outline: 'none', minWidth: 200, fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {RAG_OPTIONS.map(o => (
                    <button key={o.value} type="button"
                      onClick={() => setNewColour(o.value)} title={o.label}
                      style={{
                        width: 24, height: 24, borderRadius: '50%',
                        border: newColour === o.value ? `3px solid ${o.dot}` : '2px solid #E2E8F0',
                        background: o.dot, cursor: 'pointer', padding: 0,
                        boxShadow: newColour === o.value ? `0 0 0 2px ${o.bg}` : 'none',
                      }}
                    />
                  ))}
                </div>
                <button onClick={handleAdd} disabled={adding || !newName.trim()}
                  style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 14, fontWeight: 600, cursor: adding ? 'not-allowed' : 'pointer', opacity: !newName.trim() ? 0.5 : 1 }}>
                  {adding ? 'Adding…' : 'Add'}
                </button>
                <button onClick={() => { setShowAdd(false); setNewName(''); }}
                  style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 6, padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#718096' }}>
                  Cancel
                </button>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#718096' }}>
                Adding to <strong>{currentProductLabel}</strong>. Select <strong>red</strong> for blocked/at-risk, <strong>amber</strong> for in-progress, <strong>green</strong> for on-track/complete.
              </p>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)}
              style={{ marginTop: 8, width: '100%', background: '#fff', border: '2px dashed #CBD5E0', borderRadius: 8, padding: '12px', fontSize: 14, color: '#718096', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
              onMouseEnter={e => { e.target.style.borderColor = '#10b981'; e.target.style.color = '#10b981'; }}
              onMouseLeave={e => { e.target.style.borderColor = '#CBD5E0'; e.target.style.color = '#718096'; }}>
              + Add Stage to {currentProductLabel}
            </button>
          )}

        </div>
      </main>
    </div>
  );
}
